import React, {useEffect, useRef, useState} from 'react';
import {Badge, Card, Space, Tag, Typography} from 'antd';
import {botService, PromptHistoryResponse} from '../../services/botService';
import {AgentWorkflowStep} from '../../hooks/useAgentWebSocket';

const {Text} = Typography;
const CONNECTOR_COLOR = 'rgba(240, 180, 41, 0.35)';
const NODE_BACKGROUND = 'rgba(255, 255, 255, 0.03)';
const NODE_MIN_HEIGHT = 56;
const CONNECTOR_HORIZONTAL_GAP = 20;
const CONNECTOR_VERTICAL_GAP = 28;
const NEXT_ROW_CENTER_OFFSET = NODE_MIN_HEIGHT + CONNECTOR_VERTICAL_GAP + NODE_MIN_HEIGHT / 2;
const ARROW_SIZE = 6;
const TURN_DOT_SIZE = 8;
const ARROW_WIDTH = 10;
const ARROW_HEIGHT = 10;

type FlowNodeStatus = 'finish' | 'process' | 'error';

interface FlowNodeView {
    nodeCode: string;
    nodeName: string;
    orderNo: number;
    status: FlowNodeStatus;
    message?: string;
}

const getSnakeColumns = (_width: number): number => {
    return 5;
};

const getNodeStatusColor = (status: FlowNodeStatus): string => {
    if (status === 'error') return '#ff7875';
    if (status === 'process') return '#f0b429';
    return 'var(--pt-bull)';
};

const isSuccessTerminalStep = (step: AgentWorkflowStep): boolean =>
    step.functionName === 'output'
    || step.functionName === 'submit_final_output'
    || step.stepType === 'final_output'
    || step.stepType === 'text_output'
    || step.stepType === 'chat_output';

const getArrowStyle = (
    direction: 'left' | 'right',
    anchor: 'outer' | 'inner',
    top: string
): React.CSSProperties => {
    const horizontalStyle = anchor === 'outer'
        ? (direction === 'right'
            ? {right: `-${CONNECTOR_HORIZONTAL_GAP}px`}
            : {left: `-${CONNECTOR_HORIZONTAL_GAP}px`})
        : (direction === 'right'
            ? {right: '100%'}
            : {left: '100%'});

    return {
        position: 'absolute',
        top,
        transform: 'translateY(-50%)',
        width: `${ARROW_WIDTH}px`,
        height: `${ARROW_HEIGHT}px`,
        background: CONNECTOR_COLOR,
        clipPath: direction === 'right'
            ? 'polygon(0 0, 100% 50%, 0 100%)'
            : 'polygon(100% 0, 0 50%, 100% 100%)',
        zIndex: 0,
        ...horizontalStyle
    };
};

const getTurnDotStyle = (side: 'left' | 'right', top: string): React.CSSProperties => ({
    position: 'absolute',
    top,
    width: TURN_DOT_SIZE,
    height: TURN_DOT_SIZE,
    borderRadius: '50%',
    background: CONNECTOR_COLOR,
    boxShadow: '0 0 0 2px rgba(240, 180, 41, 0.08)',
    zIndex: 0,
    ...(side === 'right'
        ? {right: `-${CONNECTOR_HORIZONTAL_GAP + TURN_DOT_SIZE / 2}px`, transform: 'translateY(-50%)'}
        : {left: `-${CONNECTOR_HORIZONTAL_GAP + TURN_DOT_SIZE / 2}px`, transform: 'translateY(-50%)'})
});

interface PromptFlowProgressBoardProps {
    apiKeyId: number | null;
    refreshTrigger: number;
    wsConnected?: boolean;
    stepsByRun?: Record<string, AgentWorkflowStep[]>;
}

const PromptFlowProgressBoard: React.FC<PromptFlowProgressBoardProps> = ({
                                                                             apiKeyId,
                                                                             refreshTrigger,
                                                                             wsConnected,
                                                                             stepsByRun
                                                                         }) => {
    const [records, setRecords] = useState<PromptHistoryResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [boardWidth, setBoardWidth] = useState(0);
    const boardRef = useRef<HTMLDivElement | null>(null);

    const loadFlowRecords = async (silent = false) => {
        if (!apiKeyId) {
            setRecords([]);
            if (!silent) {
                setLoading(false);
            }
            return;
        }
        if (!silent) {
            setLoading(true);
        }
        try {
            const response = await botService.getProcessingPrompts(apiKeyId, 5);
            if (!response.success || !response.data) {
                if (!silent) {
                    setRecords([]);
                    setLoading(false);
                }
                return;
            }

            const isTerminalStatus = (item: PromptHistoryResponse) =>
                item.status === 'FAILED' || item.status === 'SUCCESS';

            const sortedRecords = [...response.data].sort((a, b) => (b.createdTime || 0) - (a.createdTime || 0));
            const sessionMap = new Map<string, PromptHistoryResponse>();

            sortedRecords.forEach(item => {
                if (isTerminalStatus(item)) {
                    return;
                }
                const key = item.chatSessionId ? `session-${item.chatSessionId}` : `record-${item.decisionId || `temp-${Math.random()}`}`;
                const existed = sessionMap.get(key);
                const hasFlowNodes = (r: PromptHistoryResponse) => Boolean(r.flowNodes && r.flowNodes.length > 0);
                if (!existed) {
                    sessionMap.set(key, item);
                    return;
                }
                if (hasFlowNodes(item) && !hasFlowNodes(existed)) {
                    sessionMap.set(key, item);
                    return;
                }
                if ((item.createdTime || 0) > (existed.createdTime || 0)) {
                    sessionMap.set(key, item);
                }
            });

            const processingRecords = Array.from(sessionMap.values()).slice(0, 5);
            setRecords(processingRecords);
        } catch (e) {
            if (!silent) {
                setRecords([]);
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadFlowRecords(false);
    }, [apiKeyId, refreshTrigger]);

    useEffect(() => {
        if (!apiKeyId) {
            return;
        }
        const timer = setInterval(() => {
            loadFlowRecords(true);
        }, 30000);
        return () => clearInterval(timer);
    }, [apiKeyId]);

    useEffect(() => {
        const element = boardRef.current;
        if (!element || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry) {
                setBoardWidth(entry.contentRect.width);
            }
        });
        observer.observe(element);
        setBoardWidth(element.getBoundingClientRect().width);
        return () => observer.disconnect();
    }, []);

    // 从 WebSocket 步骤构建动态流程节点
    const buildWsFlowNodes = (steps: AgentWorkflowStep[]): FlowNodeView[] => {
        return steps.map((step, index) => ({
            nodeCode: step.functionName || step.step || 'step',
            nodeName: mapStepToName(step),
            orderNo: index + 1,
            status: step.functionName === '__agent_failed__'
                ? 'error'
                : isSuccessTerminalStep(step)
                    ? 'finish'
                    : (index === steps.length - 1 ? 'process' : 'finish'),
            message: step.summary || step.result?.substring(0, 50) || undefined
        }));
    };

    // 从轮询记录落库的 flowNodes 构建流程节点（WebSocket 断连/无实时推送时的兜底展示）
    const buildRecordFlowNodes = (record: PromptHistoryResponse): FlowNodeView[] => {
        const nodes = record.flowNodes || [];
        const mapStatus = (s?: string): FlowNodeStatus =>
            s === 'RUNNING' ? 'process' : (s === 'FAILED' ? 'error' : 'finish');
        return nodes.map(node => ({
            nodeCode: node.nodeCode || 'step',
            nodeName: node.nodeName || node.nodeCode || '步骤',
            orderNo: node.orderNo ?? 0,
            status: mapStatus(node.status),
            message: node.message || undefined
        }));
    };

    const mapStepToName = (step: AgentWorkflowStep): string => {
        const stepType = step.stepType;
        const stepTypeNameMap: Record<string, string> = {
            'final_output': 'Agent决策完成',
            'text_output': '文本输出',
            'chat_output': '聊天回复',
            'output_validation': '产出校验',
            'output_validation_failed': '产出校验失败',
            'output_validation_aborted': '产出校验终止',
            'output_repair_feedback': '自动修复回灌',
            'tool_call_forced_output': '强制提交决策',
            'tool_call_debug_blocked': '调试拦截',
            'tool_call_suspend': '挂起等待子智能体',
            'tool_call_failed': '工具执行失败',
            'tool_call_validation_failed': '工具校验失败',
        };

        if (stepType && stepTypeNameMap[stepType]) {
            return stepTypeNameMap[stepType];
        }

        const functionNameMap: Record<string, string> = {
            'get_kline': '获取K线数据',
            'get_position': '查询持仓',
            'get_balance': '查询账户余额',
            'trade': '执行交易',
            'cancel_order': '撤销订单',
            'set_stop_order': '设置止损止盈',
            'get_candle_patterns': 'K线形态识别',
            'get_long_short_ratio': '查询多空比',
            'get_zone_patterns': '价格区域分析',
            'get_radar': '市场雷达扫描',
            'get_topN': '合约排行榜',
            'get_user_exchange': '交易所查询',
            'get_user_trade_config': '交易配置查询',
            'collect_agent_calls': '汇总智能体调用',
            'submit_task_plan': '提交任务计划',
            'call_agent': '调用子智能体',
            'close_position': '平仓',
            'place_order': '下单',
            'output': '最终决策',
            'submit_final_output': '最终决策',
            'AGENT_EXECUTING': '智能体执行中',
            'DISPATCHED': '调度完成',
            'unknown': '模型响应解析中',
            '__agent_failed__': '执行失败',
        };

        if (step.functionName && functionNameMap[step.functionName]) {
            return functionNameMap[step.functionName];
        }

        const stepNameMap: Record<string, string> = {
            'AGENT_EXECUTING': '智能体执行中',
            'DISPATCHED': '调度完成',
            'EXECUTING': '执行中',
            'EXECUTED': '执行成功',
            'FAILED': '执行失败',
            'REJECTED': '已驳回',
        };
        if (step.step) {
            return stepNameMap[step.step] || step.step;
        }

        if (step.functionName) {
            return step.functionName;
        }
        if (stepType) {
            return stepType;
        }
        return '处理中';
    };

    const renderSnakeFlow = (nodes: FlowNodeView[]) => {
        const columns = getSnakeColumns(boardWidth);
        return (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    columnGap: `${CONNECTOR_HORIZONTAL_GAP}px`,
                    rowGap: `${CONNECTOR_VERTICAL_GAP}px`,
                    width: '100%',
                    overflow: 'visible'
                }}
            >
                {nodes.map((node, index) => {
                    const row = Math.floor(index / columns);
                    const indexInRow = index % columns;
                    const rowItemCount = Math.min(columns, nodes.length - row * columns);
                    const isLeftToRightRow = row % 2 === 0;
                    const visualColumn = isLeftToRightRow ? indexInRow : columns - indexInRow - 1;
                    const isLastNode = index === nodes.length - 1;
                    const wrapsToNextRow = !isLastNode && indexInRow === rowItemCount - 1;
                    const hasNextInSameRow = !isLastNode && !wrapsToNextRow;
                    const connectorSide = isLeftToRightRow ? 'right' : 'left';
                    const arrowPointSide = isLeftToRightRow ? 'right' : 'left';
                    const nextRowArrowSide = isLeftToRightRow ? 'left' : 'right';
                    const statusColor = getNodeStatusColor(node.status);
                    const isProcessingNode = node.status === 'process';

                    return (
                        <div
                            key={`${node.nodeCode}-${node.orderNo}-${index}`}
                            style={{
                                gridColumn: `${visualColumn + 1} / span 1`,
                                gridRow: `${row + 1} / span 1`,
                                minWidth: 0,
                                position: 'relative',
                                overflow: 'visible'
                            }}
                        >
                            {hasNextInSameRow && (
                                <>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                            width: `${CONNECTOR_HORIZONTAL_GAP}px`,
                                            height: '3px',
                                            background: CONNECTOR_COLOR,
                                            borderRadius: '999px',
                                            zIndex: 0
                                        }}
                                    />
                                    <div
                                        style={getArrowStyle(arrowPointSide, 'outer', '50%')}
                                    />
                                </>
                            )}
                            {wrapsToNextRow && (
                                <>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                            width: `${CONNECTOR_HORIZONTAL_GAP}px`,
                                            height: '3px',
                                            background: CONNECTOR_COLOR,
                                            borderRadius: '999px',
                                            zIndex: 0
                                        }}
                                    />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(-1px)',
                                            [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                            width: '3px',
                                            height: `${NEXT_ROW_CENTER_OFFSET - NODE_MIN_HEIGHT / 2}px`,
                                            background: CONNECTOR_COLOR,
                                            borderRadius: '999px',
                                            zIndex: 0
                                        }}
                                    />
                                    <div
                                        style={getTurnDotStyle(connectorSide, '50%')}
                                    />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: `${NEXT_ROW_CENTER_OFFSET}px`,
                                            transform: 'translateY(-50%)',
                                            [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                            width: `${CONNECTOR_HORIZONTAL_GAP}px`,
                                            height: '3px',
                                            background: CONNECTOR_COLOR,
                                            borderRadius: '999px',
                                            zIndex: 0
                                        }}
                                    />
                                    <div
                                        style={getTurnDotStyle(connectorSide, `${NEXT_ROW_CENTER_OFFSET}px`)}
                                    />
                                    <div
                                        style={getArrowStyle(nextRowArrowSide, 'inner', `${NEXT_ROW_CENTER_OFFSET}px`)}
                                    />
                                </>
                            )}
                            <div
                                title={node.message || node.nodeName}
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                    height: `${NODE_MIN_HEIGHT}px`,
                                    padding: '8px 12px',
                                    borderRadius: '12px',
                                    background: isProcessingNode
                                        ? 'linear-gradient(90deg, rgba(240, 180, 41, 0.12), rgba(255, 255, 255, 0.04))'
                                        : NODE_BACKGROUND,
                                    border: `1px solid ${node.status === 'process' ? 'rgba(240, 180, 41, 0.55)' : 'rgba(255,255,255,0.12)'}`,
                                    boxShadow: node.status === 'process'
                                        ? '0 0 0 1px rgba(240, 180, 41, 0.16) inset, 0 0 18px rgba(240, 180, 41, 0.12)'
                                        : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    animation: isProcessingNode ? 'flow-node-pulse 1.8s ease-in-out infinite' : undefined
                                }}
                            >
                                <div
                                    style={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: statusColor,
                                        color: '#111',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        flex: '0 0 auto',
                                        boxShadow: isProcessingNode ? '0 0 0 0 rgba(240, 180, 41, 0.45)' : 'none',
                                        animation: isProcessingNode ? 'flow-node-dot-pulse 1.8s ease-in-out infinite' : undefined
                                    }}
                                >
                                    {node.orderNo}
                                </div>
                                <div style={{minWidth: 0, flex: 1}}>
                                    <Text
                                        style={{
                                            fontSize: 12,
                                            color: 'rgba(255, 255, 255, 0.92)',
                                            display: 'block',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {node.nodeName}
                                    </Text>
                                </div>
                                <Text
                                    style={{
                                        fontSize: 11,
                                        color: statusColor,
                                        flex: '0 0 auto',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {node.status === 'process' ? '处理中' : (node.status === 'error' ? '失败' : '完成')}
                                </Text>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const hasWsData = wsConnected && stepsByRun && Object.keys(stepsByRun).length > 0;

    // WebSocket 已覆盖展示的 sessionId 集合（用于轮询落库卡去重，避免同一会话显示两张流程卡）
    const wsSessionIds = new Set<string>();
    if (stepsByRun) {
        Object.values(stepsByRun).forEach(steps => {
            const sid = steps.find(s => s.sessionId)?.sessionId;
            if (sid) {
                wsSessionIds.add(sid);
            }
        });
    }

    // 从轮询记录中提取最近的 decisionId/sessionId 用于展示
    const currentRecord = records.find(r => r.decisionId || r.chatSessionId);
    const currentDecisionId = currentRecord?.decisionId;
    const currentSessionId = currentRecord?.chatSessionId;

    if (!apiKeyId || (records.length === 0 && !loading && !hasWsData)) {
        return null;
    }

    return (
        <>
            <style>
                {`
                  @keyframes flow-node-pulse {
                    0%, 100% { transform: translateY(0); box-shadow: 0 0 0 1px rgba(240, 180, 41, 0.16) inset, 0 0 16px rgba(240, 180, 41, 0.10); }
                    50% { transform: translateY(-1px); box-shadow: 0 0 0 1px rgba(240, 180, 41, 0.26) inset, 0 0 26px rgba(240, 180, 41, 0.22); }
                  }
                  @keyframes flow-node-dot-pulse {
                    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(240, 180, 41, 0.32); }
                    60% { transform: scale(1.08); box-shadow: 0 0 0 8px rgba(240, 180, 41, 0); }
                  }
                `}
            </style>
            <Card
                style={{
                    backgroundColor: 'var(--pt-bg-panel)',
                    border: '1px solid var(--pt-border)',
                    borderRadius: '8px'
                }}
                styles={{
                    body: {
                        padding: '12px 16px',
                        maxHeight: '240px',
                        overflowY: 'auto'
                    }
                }}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                    <Space>
                        <Text style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: 13}}>Flow</Text>
                        <Badge status={wsConnected ? 'processing' : 'default'}
                               color={wsConnected ? 'var(--pt-bull)' : undefined}/>
                        <Text style={{color: 'rgba(255, 255, 255, 0.45)', fontSize: 11}}>
                            {wsConnected ? '实时' : '轮询模式'}
                        </Text>
                    </Space>
                    <Tag color="blue">{records.length} 条执行中</Tag>
                </div>
                <div ref={boardRef} style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                    {/* WebSocket 实时步骤 — 按run分组展示 */}
                    {hasWsData && Object.entries(stepsByRun || {}).map(([runId, steps]) => (
                        <div key={runId} style={{
                            padding: '8px 10px',
                            border: '1px solid rgba(240, 180, 41, 0.3)',
                            borderRadius: 6,
                            background: 'rgba(240, 180, 41, 0.05)'
                        }}>
                            <div style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8}}>
                                <Tag color="gold">RealTime</Tag>
                                <Text style={{color: 'rgba(255, 255, 255, 0.85)'}}>Processing</Text>
                                <Text style={{color: 'rgba(255, 255, 255, 0.45)', fontFamily: 'monospace'}}>
                                    #{runId}
                                </Text>
                                {(currentDecisionId || currentSessionId) && (
                                    <Text style={{color: 'rgba(255, 255, 255, 0.45)'}}>
                                        {currentDecisionId ? `#D ${currentDecisionId}` : ''}{currentDecisionId && currentSessionId ? ' · ' : ''}{currentSessionId ? `#S ${currentSessionId}` : ''}
                                    </Text>
                                )}
                            </div>
                            {renderSnakeFlow(buildWsFlowNodes(steps))}
                        </div>
                    ))}
                    {/* 轮询记录落库 flowNodes — WebSocket 无推送/断连时兜底展示（按 session 去重，避免与实时卡重复） */}
                    {records.map(record => {
                        const recordSessionId = record.chatSessionId != null ? String(record.chatSessionId) : null;
                        // 该 session 已有 WebSocket 实时流程卡，跳过落库兜底卡避免重复展示
                        if (recordSessionId && wsSessionIds.has(recordSessionId)) {
                            return null;
                        }
                        const nodes = buildRecordFlowNodes(record);
                        if (nodes.length === 0) {
                            return null;
                        }
                        return (
                            <div key={`record-${record.chatSessionId || record.decisionId || 'unknown'}`}
                                 style={{
                                     padding: '8px 10px',
                                     border: '1px solid rgba(255,255,255,0.12)',
                                     borderRadius: 6,
                                     background: 'rgba(255,255,255,0.03)'
                                 }}>
                                <div style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8}}>
                                    <Tag color="blue">Flow</Tag>
                                    <Text
                                        style={{color: 'rgba(255, 255, 255, 0.85)'}}>{record.agentName || 'Agent'}</Text>
                                    {record.chatSessionId && (
                                        <Text style={{color: 'rgba(255, 255, 255, 0.45)', fontFamily: 'monospace'}}>
                                            #S {record.chatSessionId}
                                        </Text>
                                    )}
                                    <Text style={{color: 'rgba(240, 180, 41, 0.9)', fontSize: 11}}>处理中</Text>
                                </div>
                                {renderSnakeFlow(nodes)}
                            </div>
                        );
                    })}
                </div>
            </Card>
        </>
    );
};

export default PromptFlowProgressBoard;
